import { ACCESS } from '@/features/access/access';
import { useRoleAccess } from '@/features/access/use-role-access';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { AlertBanner, AlertBannerAction } from '@/components/molecules/AlertBanner';
import {
  ALERT_BANNER_ACTION_CLASS,
  splitLeadSentence,
} from '@/components/molecules/alert-banner-helpers';
import { Calendar, Clock, CreditCard, PauseCircle, Refresh, TrendingUp } from '@/icons/Icon';
import type { BillingBannerKind } from '@/lib/billing';
import { cn } from '@/lib/utils';
import { notifyApiError, notifyWarning } from '@/lib/toast';
import { useOrgRole } from '@/features/settings/queries';
import { BillingPortalButton } from '@/features/billing/BillingPortalButton';
import { billingKeys, useBillingSummary, useExtendTrialMutation } from './queries';
import {
  BILLING_BANNER_VARIANT,
  billingBannerAction,
  billingBannerMessage,
  isTrialAlreadyExtended,
} from './billing-banner';
import { trackTrialBannerInteracted } from './trial-banner-analytics';

// Kept unexported so this module exports only React components (Fast Refresh).
const ICON_BY_KIND: Record<BillingBannerKind, typeof CreditCard> = {
  SUBSCRIPTION_PAUSED: PauseCircle,
  PAYMENT_FAILED: CreditCard,
  SUBSCRIPTION_SCHEDULED_CHANGE: Calendar,
  TRIAL_ENDING: Clock,
  TRIAL_EXTENSION_AVAILABLE: Refresh,
  FREE_PLAN_UPGRADE: TrendingUp,
};

/**
 * The app-wide billing banner. Mounted once in `AppShell`, so it rides above
 * every authed page rather than being buried in Settings → Organization (where
 * a hardcoded PAST_DUE notice used to live, visible only to owners who happened
 * to be on that screen, and without the deadline).
 *
 * It renders exactly what `GET /billing/summary` says: the backend owns the
 * precedence between SUBSCRIPTION_PAUSED / PAYMENT_FAILED /
 * SUBSCRIPTION_SCHEDULED_CHANGE / TRIAL_ENDING / TRIAL_EXTENSION_AVAILABLE /
 * FREE_PLAN_UPGRADE, and
 * this component never second-guesses it. `null` kind, a loading summary, or a
 * failed summary all render nothing — a billing nudge must never block, shift,
 * or error the app it sits on top of, so there is deliberately no skeleton and
 * no retry affordance here (the sidebar `PlanCard` owns the visible failure
 * state for this same query key).
 *
 * CTAs are owner-gated to mirror the server: extend-trial and customer-portal
 * are both `@RequiresOrgRole(OWNER)`, and the upgrade card is owner-only, so a
 * member sees the warning with copy pointing at their workspace owner instead of
 * a button that can only 403. See `billing-banner.ts` for the copy + action
 * rules.
 */
export function BillingAlertBanner({ className }: { className?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data } = useBillingSummary();
  const { isOrgOwner } = useOrgRole(); // copy only
  const { allowed: canManageBilling } = useRoleAccess(ACCESS.billingManage);
  const extend = useExtendTrialMutation();

  const banner = data?.banner ?? null;
  if (!banner) return null;

  const action = billingBannerAction(banner, canManageBilling);

  // Same event the sidebar `PlanCard` upgrade CTA fires (the PostHog event name
  // is fixed and shared); `kind` distinguishes which banner it came from.
  function goToPlans() {
    trackTrialBannerInteracted(data, 'upgrade_clicked', banner?.kind);
    void navigate({ to: '/billing', search: { tab: 'plans' } });
  }

  function handleExtend() {
    trackTrialBannerInteracted(data, 'extend_clicked', banner?.kind);
    extend.mutate(undefined, {
      onError: (err) => {
        if (isTrialAlreadyExtended(err)) {
          // Expected, not exceptional: our cached summary was stale. Say so
          // plainly and refresh it so the spent CTA disappears.
          notifyWarning(
            "You've already extended your trial. Trials can only be extended once.",
          );
          void queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
          return;
        }
        notifyApiError(err, "Couldn't extend your trial");
      },
    });
  }

  // The pending-change ACTION rides on the summary, not on `banner` — the server
  // splits the effective date (banner.graceEndsAt) from what is actually
  // happening (scheduledChange.action). The first sentence leads in bold; the
  // copy itself is unchanged.
  const { lead, rest } = splitLeadSentence(
    billingBannerMessage(banner, isOrgOwner, data?.scheduledChange ?? null),
  );

  return (
    <AlertBanner
      variant={BILLING_BANNER_VARIANT[banner.kind]}
      icon={ICON_BY_KIND[banner.kind]}
      title={lead}
      appearance="solid"
      data-banner-kind={banner.kind}
      className={cn('shrink-0', className)}
      action={
        action === 'portal' ? (
          <BillingPortalButton
            label="Update payment method"
            variant="outline"
            buttonClassName={ALERT_BANNER_ACTION_CLASS}
            // The no-portal explanation replaces the button: plain text in the
            // banner's own colour, never styled as a (dead) button.
            fallbackClassName="font-semibold text-current"
          />
        ) : action === 'extend-trial' ? (
          <AlertBannerAction disabled={extend.isPending} onClick={handleExtend}>
            {extend.isPending ? 'Extending…' : 'Extend trial'}
          </AlertBannerAction>
        ) : action === 'upgrade' ? (
          <AlertBannerAction onClick={goToPlans}>Upgrade plan</AlertBannerAction>
        ) : undefined
      }
    >
      {rest}
    </AlertBanner>
  );
}
